// @flow
import {Platform} from 'react-native';
import {takeEvery, put, call, select, all} from 'redux-saga/effects';
import {setSIMCardNumber} from '../SignUp/SIMCardScan/actions';
import {
  EsimActivationStatus,
  getESimActivationStatus,
  getTrpcESimManager,
  getEsimCompatibility,
} from './eSIM';
import HandleErrorService from '../../lib/handleError';
import {esimSerialNumberSelector} from './selectors';
import config from '../../config/shippingConfig/kaizen';
import type {EsimProductActionType} from './actions';
import {setLoading} from '../LoadingStatus';
import {
  getESimProductPriceFailure,
  getESimProductPriceSuccess,
  fetchAvailableSimSuccess,
  fetchAvailableSimFailure,
  getChooseEsimRouteNameSuccess,
  getChooseEsimRouteNameFailure,
  setIsEsimCompatible,
} from './actions';
import {tripica} from '../../api';
import {isLoggedInSelector} from '../Authentication';
import WizzeeLog from '../../lib/wizzeeLog';

export function* getAvailableSimSaga(
  action: EsimProductActionType,
): Generator<*, *, *> {
  if (action.type !== 'FETCH_AVAILABLE_SIM') {
    return; // for flow
  }
  yield put(setLoading('getAvailableSimSaga', true));
  try {
    const {simType, resourceSerialNumber} = action.payload;
    const simResource = yield call(
      [tripica, 'getAvailableSim'],
      simType,
      resourceSerialNumber,
    );
    WizzeeLog.log(
      '>>> getAvailableSim() - simResource: ',
      JSON.stringify(simResource),
    );
    yield put(setSIMCardNumber(simResource.resourceSerialNumber));
    yield put(fetchAvailableSimSuccess(simResource));
  } catch (err) {
    WizzeeLog.warn('[saga] getAvailableSim', err);
    yield put(fetchAvailableSimFailure(err));
    HandleErrorService.showToastError(err);
  } finally {
    yield put(setLoading('getAvailableSimSaga', false));
  }
}

export function* installESim(
  action: EsimProductActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_INSTALL_ESIM') {
    return; // for flow
  }
  try {
    const esimSerialNumber = yield select(esimSerialNumberSelector);
    WizzeeLog.log('>>> installESim() - esimSerialNumber: ', esimSerialNumber);
    const resources = yield call([tripica.api, 'fetchResources']);
    WizzeeLog.log('>>> installESim() - resources: ', resources);
    const resource = resources.find(
      ({resourceSerialNumber}) => resourceSerialNumber === esimSerialNumber,
    );
    if (!resource) {
      WizzeeLog.error(
        '>>> installESim() - eSIM serialNumber %s could not be found in resources:',
        esimSerialNumber,
      );
      throw new Error("Une erreur est survenue avec l'eSIM");
    }

    WizzeeLog.log('>>> installESim() - resource: ', JSON.stringify(resource));

    // We do not use ICCID because of the quantity limitation of eSIM profiles
    const {MatchingId, Vendor} = resource.characteristics;
    WizzeeLog.log('>>> installESim() - MatchingId: ', MatchingId);
    WizzeeLog.log('>>> installESim() - Vendor: ', Vendor);
    const smdpAddress = config.eSimCarrierApp['VALID']?.smdpAddress ?? '';
    WizzeeLog.log('>>> installESim() - smdpAddress: ', smdpAddress);

    const result =
      Platform.OS === 'android'
        ? yield call(getTrpcESimManager()?.installESim, smdpAddress, MatchingId)
        : yield call(
            getTrpcESimManager()?.installESim,
            smdpAddress,
            MatchingId,
            true /* use universal link to install eSIM on iOS */,
          );

    WizzeeLog.log('>>> installESim() - result: ', result);

    const activationStatus = getESimActivationStatus(result);
    WizzeeLog.log('>>> installESim() - activationStatus: ', activationStatus);

    // yield call(this.props.goToNextPage, {params: {result: activationStatus}});
  } catch (err) {
    // yield call(this.props.goToNextPage, {
    //   params: {result: EsimActivationStatus.FAIL},
    // });
    WizzeeLog.warn('[saga] installESim', err);
    HandleErrorService.showToastError(err);
  }
}

export function* getESimProductPriceSaga(
  action: EsimProductActionType,
): Generator<*, *, *> {
  try {
    const eSimProductPrice = yield call([tripica, 'getESimProductPrice']);
    yield put(getESimProductPriceSuccess(eSimProductPrice));
  } catch (e) {
    WizzeeLog.warn('[saga] getESimProductPriceSaga', e);
    yield put(getESimProductPriceFailure(e));
    HandleErrorService.showToastError(e);
  }
}

export function* getEsimRouteNameSaga(
  action: EsimProductActionType,
): Generator<*, *, *> {
  if (action.type !== 'GET_CHOOSE_ESIM_ROUTENAME') {
    return;
  }

  try {
    const isESimCompatible = yield call(getEsimCompatibility);
    const hasAuthToken = yield select(isLoggedInSelector);
    const defaultRoute = !hasAuthToken
      ? 'createAccount'
      : Platform.OS === 'web'
        ? 'identityRequirements'
        : '';
    if (isESimCompatible) {
      yield put(setIsEsimCompatible(true));
      yield put(getChooseEsimRouteNameSuccess('chooseSimOrEsim'));
    } else {
      yield put(setIsEsimCompatible(false));
      yield put(getChooseEsimRouteNameSuccess(defaultRoute));
    }
  } catch (err) {
    WizzeeLog.warn('[saga] getEsimRouteNameSaga', err);
    yield put(setIsEsimCompatible(false));
    yield put(getChooseEsimRouteNameFailure(err));
  }
}

export default function* (): Generator<*, *, *> {
  yield all([
    takeEvery('SIGNUP_INSTALL_ESIM', installESim),
    takeEvery(
      'FETCH_AVAILABLE_SIM',
      getAvailableSimSaga,
    ),
    takeEvery('GET_ESIM_PRODUCT_PRICE', getESimProductPriceSaga),
    takeEvery('GET_CHOOSE_ESIM_ROUTENAME', getEsimRouteNameSaga),
  ]);
}
